import React, { useEffect } from 'react';
import './Requests.scss';
import { useTranslation } from 'react-i18next';
import { Table } from 'antd';
import LeaveTypes from '../LeaveTypes';
import { ReactComponent as InfoIcon } from '@iba-team/portal-react-components/dist/svg/outline/information.svg';
import { ReactComponent as CheckboxIcon } from '@iba-team/portal-react-components/dist/svg/outline/check.svg';
import { ReactComponent as CloseIcon } from '@iba-team/portal-react-components/dist/svg/outline/x.svg';
import { observer } from 'mobx-react';
import { RequestsStore } from '../../store/Requests';
import { Layout, Tooltip, UserPreview, Spinner } from '@iba-team/portal-react-components/dist';
import {
  approveRequestModalCall,
  rejectRequestModalCall,
  requestDetailsModalCall
} from '../../containers/MainWrapper/MainWrapper';
import { BREAKPOINT_BIG, BREAKPOINT_MD, BREAKPOINT_MLT_MD, BREAKPOINT_SM } from '../../consts';
import { useWindowSize } from 'react-use';
import { LeaveTypesStore } from '../../store/LeaveTypesList';
import { useDeviceSizeThreshold } from '../../utils/hooks';
import { format } from 'date-fns';
import { createPreciseDate } from '../../utils/formatDateAndTime';

export const Requests = observer(() => {
  const { t } = useTranslation();
  const { getRequests, requestsList, images, fetchPortraitUrl, isRequestsFetched,isRequestElementsAvatarsFetched, temporaryRequests } = RequestsStore;
  const { height } = useWindowSize();
  const is576Screen = useDeviceSizeThreshold(BREAKPOINT_SM);
  const is670Screen = useDeviceSizeThreshold(BREAKPOINT_MLT_MD);
  const isTablet = useDeviceSizeThreshold(BREAKPOINT_MD);
  const isBigScreen = useDeviceSizeThreshold(BREAKPOINT_BIG);
  const {areAllLeaveTypesFetched, allLeaveTypes, getAllLeaveTypes} = LeaveTypesStore;
  const isComponentReady = (isRequestsFetched && isRequestElementsAvatarsFetched);
  const columnClassName = 'requests-table-cell';

  useEffect(() => {
    getRequests();
  },[]);

  useEffect(()=>{
    if (!areAllLeaveTypesFetched) {
      getAllLeaveTypes();
    }
  },[]);

  useEffect(()=>{
    if(isRequestsFetched){
      fetchPortraitUrl();
    }
  },[isRequestsFetched]);

  const findEqualUrl = (value: string)=> {
    const res = images.find(item=> item.originalUrl === value);
    return res?.blob;
  };

  const NO_REQUESTS = !requestsList.length;
  const columns = [
    {
      title: `${t('employee')}`,
      dataIndex: 'employee',
      key: 'employee',
      width: is576Screen ? 130 : is670Screen ? 150 : isTablet ? 180 :  isBigScreen ? 240 :  300,
      className: columnClassName
    },
    {
      title: `${t('type')}`,
      dataIndex: 'type',
      key: 'type',
      width: is576Screen ? 40 : isTablet ? 90 : undefined,
      className: columnClassName
    },
    {
      title: `${t('fromTo')}`,
      dataIndex: 'fromTo',
      key: 'fromTo',
      width: isTablet ? 90 : undefined,
      className: columnClassName
    },
    {
      title: `${t('actions')}`,
      dataIndex: 'actions',
      key: 'actions',
      width: 80,
      className: columnClassName
    }
  ];

  const dataSource = requestsList.map( i => {
    const url = findEqualUrl(i.portraitUrl);
    return({
      key: i.id,
      employee: <UserPreview avatarLink={url} showDefaultImage username={i.employeeName} description={i.employeePosition}/>,
      type: <LeaveTypes type={i.requestName} leaveTypes={allLeaveTypes} infoMessageVisible={true}/>,
      fromTo: <div>{format(createPreciseDate(i.startDate), 'dd.MM.yyyy')}-{format(createPreciseDate(i.endDate), 'dd.MM.yyyy')}</div>,
      actions: <div style={{display: temporaryRequests.findIndex(r => r.id === i.id) !== -1 ? 'none' : 'block' }}>
        <Tooltip title={ t('details') }>
          <InfoIcon className={i.isPostfactum ? 'redIcon' : ''} onClick={() => requestDetailsModalCall.show(i)}/>
        </Tooltip>
        <Tooltip title={t('rejectRequest')}>
          <CloseIcon className={'closeIcon'} onClick={(e) => {
            e.stopPropagation();
            rejectRequestModalCall.show(i);
          }}/>
        </Tooltip>
        <Tooltip title={t('approveRequest')}>
          <CheckboxIcon className={'approveIcon'} onClick={(e) => {
            e.stopPropagation();
            approveRequestModalCall.show(i);
          }}/>
        </Tooltip>
      </div>
    });
  });

  const getRootClassName = () => {
    const baseClass = 'requests';
    if(!isComponentReady){
      return `${baseClass} request_loading`;
    }
    if (NO_REQUESTS) {
      return `${baseClass} requests_unset`;
    }
    return baseClass;
  };

  return <Layout allowBaseMargins
    header={{layoutName: <h3> {`${t('requests')} (${dataSource.length ?? 0})`} </h3>}}
    className={getRootClassName()}
  >
    {isComponentReady ?
      <>
        {dataSource.length ? <Table
          dataSource={dataSource}
          columns={!is670Screen ? columns: columns.filter(i => i.key === 'employee' || i.key === 'type' || i.key === 'fromTo')}
          className={'requests_table'}
          rowClassName={'requests-table-row-class'}
          pagination={false}
          scroll={isTablet ? { y: 200 } : height < 760 ? { y: 140 } : { y: 240 }}
          onRow={(record) => {
            return {
              onClick: () => {
                const res = requestsList.find(  i => i.id ===  record.key);
                requestDetailsModalCall.show({...res});
              },
            };
          }}/> :
          <div className={'requests_none'}>{ t('noRequestsDescr') }</div>
        }
      </>
      :
      <div className={'spinner_wrapper'}>
        <Spinner/>
      </div>
    }
  </Layout>;
});
